import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import Editor from "@monaco-editor/react";
import { Loader2, Play, Save, Rocket, Trash2, RotateCcw, FileCode2, Plus, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const DEFAULT_RENDERER = `// Deterministic V5 renderer — must define renderProject(ctx, W, H, state). NO AI.
// This runs in the browser for BOTH live preview and export (identical output).
function renderProject(ctx, W, H, state) {
  ctx.clearRect(0, 0, W, H);
  const bg = state.background || {};
  if (bg.type === "solid") { ctx.fillStyle = bg.color || "#000"; ctx.fillRect(0, 0, W, H); }
  else if (bg.type === "gradient") {
    const a = ((bg.angle || 90) * Math.PI) / 180;
    const g = ctx.createLinearGradient(W/2 - Math.cos(a)*W/2, H/2 - Math.sin(a)*H/2, W/2 + Math.cos(a)*W/2, H/2 + Math.sin(a)*H/2);
    g.addColorStop(0, bg.color || "#000"); g.addColorStop(1, bg.color2 || "#333");
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  }
  for (const l of (state.layers || [])) {
    ctx.save();
    ctx.globalAlpha = l.opacity == null ? 1 : l.opacity;
    if (l.type === "image" && (l._maskedImg || l._img)) {
      const im = l._maskedImg || l._img;
      const scale = (l.scale == null ? 1 : l.scale) * Math.min(W / im.width, H / im.height);
      const dw = im.width * scale, dh = im.height * scale;
      ctx.translate((l.x == null ? 0.5 : l.x) * W, (l.y == null ? 0.5 : l.y) * H);
      ctx.rotate(((l.rotation || 0) * Math.PI) / 180);
      ctx.drawImage(im, -dw / 2, -dh / 2, dw, dh);
    } else if (l.type === "text") {
      const size = (l.size == null ? 0.08 : l.size) * H;
      ctx.font = (l.weight || 700) + " " + size + "px " + (l.font || "Inter") + ", sans-serif";
      ctx.textAlign = l.align || "center"; ctx.textBaseline = "middle";
      ctx.translate((l.x == null ? 0.5 : l.x) * W, (l.y == null ? 0.5 : l.y) * H);
      const text = l.uppercase ? (l.text || "").toUpperCase() : (l.text || "");
      if (l.outline && (l.outlineWidth || 0) > 0) { ctx.lineWidth = l.outlineWidth; ctx.strokeStyle = l.outlineColor || "#000"; ctx.lineJoin = "round"; ctx.strokeText(text, 0, 0); }
      ctx.fillStyle = l.color || "#fff"; ctx.fillText(text, 0, 0);
    }
    ctx.restore();
  }
}`;

const SAMPLE_STATE = {
  background: { type: "gradient", color: "#0f1115", color2: "#3b0764", angle: 120 },
  layers: [
    { id: "t1", type: "text", text: "RENDERER TEST", font: "Impact", size: 0.1, weight: 800, color: "#00F0FF", align: "center", x: 0.5, y: 0.4, opacity: 1, uppercase: true, outline: true, outlineWidth: 6, outlineColor: "#000" },
    { id: "t2", type: "text", text: "deterministic · no AI", font: "Inter", size: 0.04, weight: 600, color: "#ffffff", align: "center", x: 0.5, y: 0.55, opacity: 0.9 },
  ],
};

const STATUS_COLOR = { live: "text-[#22c55e]", draft: "text-[#f59e0b]", ready: "text-[#A1A1AA]", disabled: "text-[#71717A]" };

export default function V5CodeWorkspace() {
  const [versions, setVersions] = useState([]);
  const [liveId, setLiveId] = useState(null);
  const [builtin, setBuiltin] = useState("5.0.0");
  const [editingId, setEditingId] = useState(null);
  const [code, setCode] = useState(DEFAULT_RENDERER);
  const [label, setLabel] = useState("New renderer");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");
  const [testMsg, setTestMsg] = useState(null);
  const previewRef = useRef(null);

  const load = useCallback(async () => {
    setBusy("load");
    try {
      const r = await api.v5RendererList();
      setVersions(r.versions || []); setLiveId(r.live_id); setBuiltin(r.builtin_version);
    } catch { toast.error("Could not load renderer versions (admin only)"); }
    setBusy("");
  }, []);
  useEffect(() => { load(); }, [load]);

  const runTest = useCallback(() => {
    setTestMsg(null);
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function("ctx", "W", "H", "state", `${code}\n;return renderProject(ctx,W,H,state);`);
      const cvs = previewRef.current; if (!cvs) return;
      const W = 1080, H = 1350; cvs.width = W; cvs.height = H;
      fn(cvs.getContext("2d"), W, H, SAMPLE_STATE);
      setTestMsg({ ok: true, text: "Rendered sample successfully — safe to publish." });
    } catch (e) {
      setTestMsg({ ok: false, text: "Error: " + (e?.message || e) });
    }
  }, [code]);
  useEffect(() => { const t = setTimeout(runTest, 300); return () => clearTimeout(t); }, [runTest]);

  const newDraft = () => { setEditingId(null); setCode(DEFAULT_RENDERER); setLabel("New renderer"); setNotes(""); toast.info("Started a fresh draft from the built-in renderer"); };

  const openVersion = async (id) => {
    setBusy("open");
    try {
      const r = await api.v5RendererGet(id); const v = r.version;
      setEditingId(v.status === "draft" ? v.id : null); // live/ready are read-only → editing creates a new draft
      setCode(v.code || DEFAULT_RENDERER); setLabel(v.label || "Renderer"); setNotes(v.notes || "");
      if (v.status !== "draft") toast.info("This version is read-only — saving will create a new draft");
    } catch { toast.error("Could not open version"); }
    setBusy("");
  };

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.v5RendererSave({ id: editingId, code, label, notes });
      setEditingId(r.version.status === "draft" ? r.version.id : null);
      toast.success("Draft saved"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy("");
  };

  const publish = async (id) => {
    setBusy("publish");
    try { await api.v5RendererPublish(id); toast.success("Published live — V5 now uses this renderer"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    setBusy("");
  };
  const disable = async (id) => { setBusy("disable"); try { await api.v5RendererDisable(id); toast.success("Reverted to built-in renderer"); await load(); } catch { toast.error("Failed"); } setBusy(""); };
  const del = async (id) => { setBusy("del"); try { await api.v5RendererDelete(id); toast.success("Deleted"); if (editingId === id) newDraft(); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } setBusy(""); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="v5-code-workspace">
      <div className="mx-auto max-w-[1600px] px-4 py-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-black">V5 Code Workspace <span className="rounded bg-[#00F0FF]/15 px-2 py-0.5 text-sm text-[#00F0FF]">Phase 4</span></h1>
            <p className="text-sm text-[#A1A1AA]">Edit the deterministic renderer, test against a sample, publish a version, or roll back — no server restart. Built-in fallback v{builtin}.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={newDraft} data-testid="v5r-new" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold hover:bg-white/5"><Plus size={15} /> New draft</button>
            <button onClick={load} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-sm text-[#A1A1AA] hover:text-white"><RefreshCw size={15} /> Refresh</button>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
          {/* LEFT: editor */}
          <div className="rounded-xl border border-white/10 bg-[#0D0D0E] p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="v5r-label" className="flex-1 rounded-lg border border-white/15 bg-black/40 px-3 py-1.5 text-sm" placeholder="Version label" />
              <button onClick={save} disabled={busy === "save"} data-testid="v5r-save" className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-semibold hover:bg-white/15">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save draft</button>
            </div>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="v5r-notes" className="mb-2 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-1.5 text-xs text-[#A1A1AA]" placeholder="Notes (optional)" />
            <div className="overflow-hidden rounded-lg border border-white/10" data-testid="v5r-editor">
              <Editor height="52vh" defaultLanguage="javascript" theme="vs-dark" value={code} onChange={(v) => setCode(v || "")}
                options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, tabSize: 2 }} />
            </div>
          </div>

          {/* RIGHT: preview + versions */}
          <div className="space-y-4">
            <div className="rounded-xl border border-white/10 bg-[#0D0D0E] p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">Live test preview</div>
                <button onClick={runTest} data-testid="v5r-test" className="flex items-center gap-1.5 rounded-lg border border-[#00F0FF]/40 px-2.5 py-1 text-xs font-semibold text-[#00F0FF]"><Play size={13} /> Run test</button>
              </div>
              <canvas ref={previewRef} data-testid="v5r-preview" className="mx-auto max-h-[40vh] w-auto rounded-lg border border-white/10" />
              {testMsg && <div data-testid="v5r-test-result" className={`mt-2 rounded-lg px-3 py-2 text-xs ${testMsg.ok ? "bg-[#22c55e]/10 text-[#22c55e]" : "bg-[#ef4444]/10 text-[#ef4444]"}`}>{testMsg.text}</div>}
            </div>

            <div className="rounded-xl border border-white/10 bg-[#0D0D0E] p-3" data-testid="v5r-versions">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#A1A1AA]">Versions</div>
              {busy === "load" ? <Loader2 className="mx-auto my-4 animate-spin text-[#00F0FF]" /> : versions.length === 0 ? (
                <p className="py-3 text-center text-xs text-[#71717A]">No saved renderers yet. The built-in v{builtin} is active.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {versions.map((v) => (
                    <div key={v.id} data-testid={`v5r-version-${v.id}`} className={`rounded-lg border p-2.5 text-xs ${v.id === liveId ? "border-[#22c55e]/50 bg-[#22c55e]/[0.06]" : "border-white/10"}`}>
                      <div className="flex items-center justify-between">
                        <button onClick={() => openVersion(v.id)} className="flex items-center gap-1.5 font-semibold hover:text-[#00F0FF]"><FileCode2 size={13} /> {v.label} <span className="text-[10px] text-[#71717A]">{v.version}</span></button>
                        <span className={`font-bold uppercase ${STATUS_COLOR[v.status] || "text-white"}`}>{v.status}</span>
                      </div>
                      {v.notes && <p className="mt-1 text-[11px] text-[#71717A]">{v.notes}</p>}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {v.id !== liveId && <button onClick={() => publish(v.id)} disabled={busy === "publish"} data-testid={`v5r-publish-${v.id}`} className="flex items-center gap-1 rounded bg-[#00F0FF] px-2 py-1 font-bold text-black"><Rocket size={11} /> {v.status === "ready" ? "Roll back to this" : "Publish"}</button>}
                        {v.id === liveId && <button onClick={() => disable(v.id)} disabled={busy === "disable"} data-testid={`v5r-disable-${v.id}`} className="flex items-center gap-1 rounded border border-white/15 px-2 py-1 font-semibold"><RotateCcw size={11} /> Revert to built-in</button>}
                        {v.id !== liveId && <button onClick={() => del(v.id)} disabled={busy === "del"} data-testid={`v5r-delete-${v.id}`} className="flex items-center gap-1 rounded border border-white/15 px-2 py-1 text-[#ef4444]"><Trash2 size={11} /> Delete</button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
